import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function formatDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString();
}

// Light client-side classification for display only; server validates fully.
function entryType(entry) {
  if (typeof entry !== 'string') return 'unknown';
  const t = entry.trim();
  if (t.includes('/')) return 'cidr';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(t)) return 'ip';
  // Domain: dot-separated labels (dyndns hostname etc.)
  if (/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(t)) return 'domain';
  return 'unknown';
}

export default function IPWhitelist() {
  const [entries, setEntries] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const { toasts, success, error: toastError } = useToast();

  useEffect(() => {
    fetchEntries();
    fetchAttempts();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/ip-whitelist`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load whitelist');
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttempts = async () => {
    try {
      const res = await fetch(`${API_URL}/ip-whitelist/attempts?limit=50`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load attempts');
      const data = await res.json();
      setAttempts(data.attempts || []);
    } catch (err) {
      // Non-fatal — the attempts panel is a convenience, not core.
      console.error('Failed to fetch kiosk attempts:', err);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    if (!newEntry.trim()) {
      setError('Enter an IP address or CIDR range');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/ip-whitelist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ entry: newEntry.trim(), description: newDescription.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add entry');
      success(`Added ${data.entry.entry}`);
      setNewEntry('');
      setNewDescription('');
      fetchEntries();
    } catch (err) {
      setError(err.message);
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Remove ${entry.entry} from the whitelist?`)) return;
    setDeletingId(entry.id);
    try {
      const res = await fetch(`${API_URL}/ip-whitelist/${entry.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete entry');
      }
      success(`Removed ${entry.entry}`);
      fetchEntries();
    } catch (err) {
      toastError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Quick-add a source IP straight from the attempts list.
  const addFromAttempt = async (ip) => {
    if (!ip) return;
    if (!window.confirm(`Add ${ip} to the whitelist?`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/ip-whitelist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ entry: ip, description: 'Added from login attempts' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add entry');
      success(`Added ${ip}`);
      fetchEntries();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <ToastContainer toasts={toasts} />

      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kiosk IP Whitelist</h1>
          <p className="mt-1 text-sm text-gray-500">
            Control which locations can use kiosk PIN login. An <span className="font-medium">empty list allows all</span> locations.
            You may whitelist an exact egress IP, a CIDR range, or a <span className="font-medium">domain name</span> (e.g. a dyndns hostname) — domains are re-resolved automatically so they stay valid when an ISP rotates the public IP.
          </p>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">All locations are currently allowed.</span>{' '}
            No IP entries have been added yet, so kiosk login is open to everyone. Add the
            egress IPs of your businesses below to restrict access to specific locations only.
          </p>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">{entries.length} location{entries.length !== 1 ? 's' : ''} allowed.</span>{' '}
            Only the entries below (egress IP, CIDR range, or domain) can perform kiosk PIN login. Everyone else is blocked.
          </p>
        </div>
      )}

      {/* Add entry form */}
      <form onSubmit={handleAdd} className="bg-white shadow rounded-lg p-5 mb-6" autoComplete="off">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Add a location</h2>
        {error && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="sm:w-72">
            <label className="block text-sm font-medium text-gray-700 mb-1">IP, CIDR range, or domain *</label>
            <input
              type="text"
              autoComplete="off"
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
              placeholder="e.g. 73.128.45.9, 73.128.0.0/16, or shop1.example-dyn.net"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              autoComplete="off"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="e.g. Main shop, Front desk ISP"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add Entry'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Use the exact public egress IP, a CIDR range (e.g. <span className="font-mono">73.128.0.0/16</span>) for rotating networks,
          or a domain name (e.g. <span className="font-mono">shop1.example-dyn.net</span>) pointing at the shop's router via dyndns — its current IP is compared to the caller's egress IP.
        </p>
      </form>

      {/* Entries table */}
      <div className="bg-white shadow overflow-hidden rounded-lg mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entries.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-500">
                  No entries yet — all locations are allowed.
                </td>
              </tr>
            ) : (
              entries.map((entry) => {
                const type = entryType(entry.entry);
                return (
                  <tr key={entry.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{entry.entry}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        type === 'cidr' ? 'bg-purple-100 text-purple-800'
                        : type === 'domain' ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-700'
                      }`}>
                        {type === 'cidr' ? 'CIDR Range' : type === 'domain' ? 'Domain' : 'Exact IP'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{entry.description || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDelete(entry)}
                        disabled={deletingId === entry.id}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        {deletingId === entry.id ? 'Removing…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Recent kiosk login attempts */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Recent Kiosk Login Attempts</h2>
          <p className="mt-1 text-xs text-gray-400">
            Shows the source IP of every kiosk login try. Denied attempts reveal the egress IP you need to whitelist.
          </p>
        </div>
        {attempts.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-400">No recent kiosk login attempts.</div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quick Add</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attempts.map((a) => (
                  <tr key={a.id}>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{formatDateTime(a.created_at)}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-mono text-gray-900">{a.ip || 'unknown'}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{a.user_name || '—'}</td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      {a.allowed ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Allowed</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={a.reason}>Denied</span>
                      )}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-right">
                      {a.ip && (
                        <button
                          onClick={() => addFromAttempt(a.ip)}
                          disabled={saving}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                        >
                          + Whitelist this IP
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
